// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 17.9.0 — Tour engine hook managing driver.js lifecycle + action execution.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Note: `TourScriptStep` is the tour-script.ts TourStep (re-exported with an
// alias because `onboarding.ts` owns a separate, unrelated TourStep shape).
import type { TourScript, TourScriptStep, TourAction } from '@vn-grc/shared';
import 'driver.js/dist/driver.css';
import { driver as createDriver } from 'driver.js';
import { useLocale } from './useLocale.js';

/**
 * 44.1.0 — VI fallback helpers for TourScript / TourStep. When the active locale
 * resolves to Vietnamese and the script/step provides `title_vi` / `description_vi`,
 * the runner prefers them; otherwise it falls back to the EN field. All 28 tours
 * gain VI parallels in Sprint 44.1.8; scripts without VI keep working unchanged.
 */
function isViLocale(locale: string): boolean {
  const prefix = (locale || '').split('-')[0].toLowerCase();
  return prefix === 'vi';
}

export function resolveTourStepTitle(step: TourScriptStep, locale: string): string {
  if (isViLocale(locale) && step.title_vi && step.title_vi.trim()) return step.title_vi;
  return step.title;
}

export function resolveTourStepDescription(step: TourScriptStep, locale: string): string {
  if (isViLocale(locale) && step.description_vi && step.description_vi.trim())
    return step.description_vi;
  return step.description;
}

export function resolveTourScriptTitle(script: TourScript, locale: string): string {
  if (isViLocale(locale) && script.title_vi && script.title_vi.trim()) return script.title_vi;
  return script.title;
}

export type TourMode = 'autoplay' | 'interactive';
export type TourSpeed = 0.5 | 1 | 2;
export type TourEngineStatus = 'idle' | 'running' | 'paused' | 'finished';

interface TourEngineState {
  status: TourEngineStatus;
  currentStepIndex: number;
  mode: TourMode;
  speed: TourSpeed;
  script: TourScript | null;
}

interface TourEngineActions {
  start: (script: TourScript, mode?: TourMode, startStepIndex?: number) => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  nextStep: () => void;
  prevStep: () => void;
  setSpeed: (speed: TourSpeed) => void;
  setMode: (mode: TourMode) => void;
}

const SPEED_MULTIPLIERS: Record<TourSpeed, number> = {
  0.5: 2,
  1: 1,
  2: 0.5,
};

const DEFAULT_STEP_DURATION = 3000;
const DEFAULT_STEP_DELAY = 0;
const TYPING_DELAY_MS = 30;
const WAIT_FOR_POLL_MS = 50;
const WAIT_FOR_TIMEOUT_MS = 5000;

export function useTourEngine(): [TourEngineState, TourEngineActions] {
  const navigate = useNavigate();
  const { locale } = useLocale();
  const [state, setState] = useState<TourEngineState>({
    status: 'idle',
    currentStepIndex: 0,
    mode: (localStorage.getItem('grc_tour_mode') as TourMode) || 'autoplay',
    speed: 1,
    script: null,
  });

  const driverRef = useRef<any>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef(false);
  const popoverObserverRef = useRef<MutationObserver | null>(null);
  const interactiveClickCleanupRef = useRef<(() => void) | null>(null);
  // Generation counter: incremented on every manual navigation to prevent stale
  // autoplay timers from firing and jumping back to a previous step.
  const generationRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  function cleanup() {
    abortRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (interactiveClickCleanupRef.current) {
      interactiveClickCleanupRef.current();
      interactiveClickCleanupRef.current = null;
    }
    if (popoverObserverRef.current) {
      popoverObserverRef.current.disconnect();
      popoverObserverRef.current = null;
    }
    if (driverRef.current) {
      try {
        driverRef.current.destroy();
      } catch (_) {
        /* ignore */
      }
      driverRef.current = null;
    }
  }

  async function waitForElement(
    selector: string,
    timeout = WAIT_FOR_TIMEOUT_MS,
  ): Promise<Element | null> {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      const el = document.querySelector(selector);
      if (el) return el;
      await new Promise((r) => setTimeout(r, WAIT_FOR_POLL_MS));
      if (abortRef.current) return null;
    }
    return null;
  }

  async function executeAction(action: TourAction, timeout?: number): Promise<void> {
    if (abortRef.current) return;

    switch (action.type) {
      case 'click': {
        const el = await waitForElement(action.target, timeout);
        if (el instanceof HTMLElement) el.click();
        break;
      }
      case 'fill': {
        const el = await waitForElement(action.target, timeout);
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
          el.focus();
          // Use the correct native setter for the element type so React's
          // controlled component tracking picks up the change correctly
          const nativeValueSetter =
            el instanceof HTMLTextAreaElement
              ? Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set
              : Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
          // Use natural typing speed for short text; cap total at 1500ms for long text
          const naturalTotal = action.value.length * TYPING_DELAY_MS;
          const charDelay =
            naturalTotal > 1500 ? Math.floor(1500 / action.value.length) : TYPING_DELAY_MS;
          for (let i = 0; i <= action.value.length; i++) {
            if (abortRef.current) return;
            nativeValueSetter?.call(el, action.value.slice(0, i));
            el.dispatchEvent(new Event('input', { bubbles: true }));
            await new Promise((r) => setTimeout(r, charDelay));
          }
        }
        break;
      }
      case 'select': {
        const el = await waitForElement(action.target, timeout);
        if (el instanceof HTMLSelectElement) {
          const nativeValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLSelectElement.prototype,
            'value',
          )?.set;
          if (nativeValueSetter) {
            nativeValueSetter.call(el, action.value);
          } else {
            el.value = action.value;
          }
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
        break;
      }
      case 'hover': {
        const el = await waitForElement(action.target, timeout);
        if (el instanceof HTMLElement) {
          el.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
        }
        break;
      }
      case 'wait': {
        await new Promise((r) => setTimeout(r, action.ms));
        break;
      }
      case 'navigate': {
        navigate(action.to);
        await new Promise((r) => setTimeout(r, 500));
        break;
      }
      case 'scroll_to': {
        const el = await waitForElement(action.target, timeout);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;
      }
    }
  }

  async function showStep(script: TourScript, index: number, mode: TourMode, speed: TourSpeed) {
    const gen = generationRef.current;
    if (abortRef.current || index >= script.steps.length) {
      setState((s) => ({ ...s, status: 'finished' }));
      return;
    }

    const step = script.steps[index];
    const delay = step.delay_ms ?? DEFAULT_STEP_DELAY;

    // Navigate if needed — wait for React to process the route change before
    // polling for elements. Without this delay, waitForElement can find stale
    // elements from the OLD page (e.g. both pages use .dm-page) and highlight
    // a DOM node that is about to be unmounted.
    if (step.navigate_to) {
      navigate(step.navigate_to);
      // Allow React Router + Suspense to unmount the old page and start loading
      // the new one. 300ms is enough for the route change + lazy chunk init.
      await new Promise((r) => setTimeout(r, 300));
      if (abortRef.current) return;
    }

    // Wait for explicit wait_for selector
    if (step.wait_for) {
      await waitForElement(step.wait_for, 5000);
    }

    // Wait for target element — longer timeout after navigation since the new
    // page may still be loading (lazy chunk + API data fetches).
    const waitTimeout = step.navigate_to ? 5000 : mode === 'interactive' ? 300 : 1500;
    const targetEl = await waitForElement(step.target, waitTimeout);

    if (abortRef.current) return;

    // Optional settle delay (0 by default, only used if step explicitly sets delay_ms)
    if (delay > 0) {
      await new Promise((r) => setTimeout(r, delay));
      if (abortRef.current) return;
    }

    if (step.scroll_to !== false && targetEl) {
      targetEl.scrollIntoView({ behavior: 'instant', block: 'center' });
    }

    // Use driver.js to highlight
    if (driverRef.current) {
      try {
        driverRef.current.highlight({
          element: step.target,
          popover: {
            title: resolveTourStepTitle(step, locale),
            description: resolveTourStepDescription(step, locale),
            side: step.position || 'bottom',
            align: 'center',
          },
        });

        // After highlight, ensure the popover doesn't overlap the tour controller
        // bar which sits at the bottom of the viewport. driver.js may reposition
        // the popover across multiple frames (animation cycle), so a single
        // requestAnimationFrame isn't enough. We use a MutationObserver on the
        // popover's style attribute to catch every repositioning and correct it.
        if (popoverObserverRef.current) {
          popoverObserverRef.current.disconnect();
          popoverObserverRef.current = null;
        }

        const adjustPopover = () => {
          const popover = document.querySelector('.grc-tour-popover') as HTMLElement | null;
          const controller = document.querySelector('.grc-tour-controller') as HTMLElement | null;
          if (popover && controller) {
            const popRect = popover.getBoundingClientRect();
            const ctrlRect = controller.getBoundingClientRect();
            if (popRect.bottom > ctrlRect.top) {
              const newTop = ctrlRect.top - popRect.height - 12;
              popover.style.top = `${Math.max(8, newTop)}px`;
              popover.style.bottom = 'auto';
            }
          }
        };

        // Watch for driver.js re-positioning the popover across animation frames.
        // adjustPopover only writes style when the popover overlaps the controller bar,
        // so once corrected the condition is false and the observer stops firing.
        requestAnimationFrame(() => {
          const popover = document.querySelector('.grc-tour-popover') as HTMLElement | null;
          if (popover) {
            const observer = new MutationObserver(adjustPopover);
            observer.observe(popover, { attributes: true, attributeFilter: ['style'] });
            popoverObserverRef.current = observer;
            // Run once immediately in case driver.js already finished positioning
            adjustPopover();
          }
        });
      } catch (_) {
        // Element may not exist, continue
      }
    }

    setState((s) => ({ ...s, currentStepIndex: index }));

    // Clean up any previous interactive click listener before attaching a new one
    if (interactiveClickCleanupRef.current) {
      interactiveClickCleanupRef.current();
      interactiveClickCleanupRef.current = null;
    }

    // In auto-play mode, execute action and auto-advance
    if (mode === 'autoplay') {
      if (step.action) {
        // Pause so the user can see the highlight before the action fires.
        // Use a longer delay when the step also navigated (page needs to settle).
        if (step.action.type === 'click') {
          const preActionDelay = step.navigate_to ? 3000 : 2500;
          await new Promise((r) => setTimeout(r, preActionDelay));
        }
        if (abortRef.current || generationRef.current !== gen) return;
        await executeAction(step.action, 1500);
        if (generationRef.current !== gen) return;
        // Brief pause after action so result is visible
        if (step.action.type === 'fill') {
          await new Promise((r) => setTimeout(r, 200));
        }
      }

      if (abortRef.current) return;

      // If the user manually navigated while this step was executing, abort
      if (generationRef.current !== gen) return;

      const duration = (step.duration_ms ?? DEFAULT_STEP_DURATION) * SPEED_MULTIPLIERS[speed];
      timerRef.current = setTimeout(() => {
        if (generationRef.current !== gen) return;
        showStep(script, index + 1, mode, speed);
      }, duration);
    } else {
      // Interactive mode: clicking the target element advances to the next step.
      // For non-click actions, the action is also executed before advancing so
      // both the target click and the Next button produce the same result.
      if (targetEl instanceof HTMLElement) {
        const handleTargetClick = () => {
          interactiveClickCleanupRef.current = null;
          if (step.action && step.action.type !== 'click') {
            const action = step.action;
            executeAction(action, 1500).then(() => {
              showStep(script, index + 1, mode, speed);
            });
          } else {
            showStep(script, index + 1, mode, speed);
          }
        };
        targetEl.addEventListener('click', handleTargetClick, { once: true });
        interactiveClickCleanupRef.current = () =>
          targetEl.removeEventListener('click', handleTargetClick);
      }
    }
  }

  const start = useCallback(
    (script: TourScript, mode?: TourMode, startStepIndex = 0) => {
      cleanup();
      abortRef.current = false;
      generationRef.current++;

      const tourMode = mode || state.mode;
      localStorage.setItem('grc_tour_mode', tourMode);

      const stepIdx = Math.min(Math.max(0, startStepIndex), script.steps.length - 1);

      driverRef.current = createDriver({
        showProgress: false,
        animate: true,
        smoothScroll: true,
        allowClose: false,
        stagePadding: 8,
        stageRadius: 8,
        popoverClass: 'grc-tour-popover',
        showButtons: [],
        overlayOpacity: 0.6,
        onDestroyed: () => {
          // Tour ended externally
        },
      });

      setState({
        status: 'running',
        currentStepIndex: stepIdx,
        mode: tourMode,
        speed: state.speed,
        script,
      });

      showStep(script, stepIdx, tourMode, state.speed);
    },
    [state.mode, state.speed],
  );

  const stop = useCallback(() => {
    cleanup();
    abortRef.current = false;
    setState({
      status: 'idle',
      currentStepIndex: 0,
      mode: state.mode,
      speed: state.speed,
      script: null,
    });
  }, [state.mode, state.speed]);

  const pause = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setState((s) => ({ ...s, status: 'paused' }));
  }, []);

  const resume = useCallback(() => {
    setState((s) => {
      if (s.status === 'paused' && s.script) {
        setTimeout(() => showStep(s.script!, s.currentStepIndex, s.mode, s.speed), 0);
        return { ...s, status: 'running' };
      }
      return s;
    });
  }, []);

  const nextStep = useCallback(() => {
    setState((s) => {
      if (!s.script) return s;
      if (timerRef.current) clearTimeout(timerRef.current);
      generationRef.current++;
      // Discard any pending interactive click listener for the current step
      if (interactiveClickCleanupRef.current) {
        interactiveClickCleanupRef.current();
        interactiveClickCleanupRef.current = null;
      }
      const next = s.currentStepIndex + 1;
      if (next >= s.script.steps.length) {
        cleanup();
        abortRef.current = false;
        return { ...s, status: 'finished' };
      }
      const currentStep = s.script.steps[s.currentStepIndex];
      setTimeout(async () => {
        // Execute the current step's action before advancing (mirrors autoplay)
        if (s.mode === 'interactive' && currentStep?.action) {
          await executeAction(currentStep.action, 1500);
          if (currentStep.action.type === 'fill' || currentStep.action.type === 'select') {
            await new Promise((r) => setTimeout(r, 200));
          }
        }
        showStep(s.script!, next, s.mode, s.speed);
      }, 0);
      return { ...s, currentStepIndex: next, status: 'running' };
    });
  }, []);

  const prevStep = useCallback(() => {
    setState((s) => {
      if (!s.script || s.currentStepIndex <= 0) return s;
      if (timerRef.current) clearTimeout(timerRef.current);
      generationRef.current++;
      const prev = s.currentStepIndex - 1;
      setTimeout(() => showStep(s.script!, prev, s.mode, s.speed), 0);
      return { ...s, currentStepIndex: prev, status: 'running' };
    });
  }, []);

  const setSpeed = useCallback((speed: TourSpeed) => {
    setState((s) => ({ ...s, speed }));
  }, []);

  const setMode = useCallback((mode: TourMode) => {
    localStorage.setItem('grc_tour_mode', mode);
    setState((s) => ({ ...s, mode }));
  }, []);

  return [state, { start, stop, pause, resume, nextStep, prevStep, setSpeed, setMode }];
}
